import { Controller } from '@hotwired/stimulus';
import { generateRecoveryCode, wrapPrivateKeyWithPassword, b64encode } from '../vault_crypto';

// Last-resort recovery for another organization, run by a key holder of the organization that
// holds its shared key: the open vault decrypts the shared key, it gets re-wrapped under a
// freshly generated recovery code, and only the wrapped result goes to the server. The code
// itself never leaves the browser — it is shown so it can be handed over on the call.
export default class extends Controller {
  static targets = ['button', 'result', 'code'];
  static values = { sharedKey: String, url: String, t: Object };

  start(event) {
    event.preventDefault();

    if (window.vault?.is_open()) {
      this.recover();
    } else {
      window.vault?.openModal(() => this.recover());
    }
  }

  async recover() {
    const btn = this.buttonTarget;
    const label = btn.textContent;
    btn.disabled = true;
    btn.textContent = this.tValue.working;

    try {
      // the vault decrypts the share blob with this organization's key, yielding the
      // stranded organization's private key as raw pkcs8 bytes
      const orgPrivBytes = await window.vault.decryptMessage(this.sharedKeyValue, false);
      if (!orgPrivBytes) throw new Error('shared key could not be decrypted');

      const orgPrivKey = await crypto.subtle.importKey(
        'pkcs8',
        orgPrivBytes,
        { name: 'RSA-OAEP', hash: 'SHA-256' },
        true,
        ['decrypt']
      );

      const code = generateRecoveryCode();
      const { wrappedKey, salt, iv } = await wrapPrivateKeyWithPassword(code, orgPrivKey);

      const response = await fetch(this.urlValue, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
        },
        body: JSON.stringify({
          organization: {
            wrapped_key: b64encode(wrappedKey),
            salt: b64encode(salt),
            iv: b64encode(iv),
          },
        }),
      });
      if (!response.ok) throw new Error(`server rejected the new key: HTTP ${response.status}`);

      // only reveal the code once the server stored the key it unwraps
      this.codeTarget.value = code;
      this.resultTarget.classList.remove('hidden');
      btn.classList.add('hidden');
    } catch (e) {
      console.error('Key recovery failed:', e);
      btn.disabled = false;
      btn.textContent = label;
      alert(this.tValue.failed);
    }
  }
}
